import React from "react";
import { Chip } from "@mui/material";
import moment from "moment";
import { CoursProps } from "../../Components/App/Courses/DetailCoursV2";
import { isRelationTeachrCourse } from "../DeclarationV2/components/relationTeachr";
import { CourseTypeConfig } from "../../config/COURSE_TYPE_CONFIG";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import Functions from "../../Helpers/Functions";
import { capitalizeFirstLetter, displayStatutCourse, displayStatutCourseDetail, hasBlockingDeclarationStatus, resolveStatutCourse } from "../../Utils/Function";
import { isVerifiedTrainer, vtClientShare, vtCoursePrixTotal, vtCourseRemuneration } from "../../Utils/VerifiedTrainerAmounts";

/**
 * Statut d'une ligne de « Mes cours » repensé : la même pastille qu'aujourd'hui
 * (`displayStatutCourse` pour les statuts URSSAF, puce selon la date sinon),
 * copiée de `ListCourse` sans changement de règle.
 */

type DateStatus = { label: string; chip: React.ReactNode };

const dateBasedStatus = (course: any, isDraft: boolean): DateStatus => {
    if (isDraft) return { label: "Brouillon", chip: <Chip label="Brouillon" size="small" color="warning" variant="outlined" /> };
    const now = moment();
    const start = moment(course?.startDatetime);
    const end = moment(course?.endDatetime);
    if (start.isSame(now, "day")) return { label: "Aujourd'hui", chip: <Chip label="Aujourd'hui" size="small" color="success" variant="filled" /> };
    if (now.isBetween(start, end, "day", "[]")) return { label: "En cours", chip: <Chip label="En cours" size="small" color="info" variant="filled" /> };
    if (start.isAfter(now)) return { label: "À venir", chip: <Chip label="À venir" size="small" color="primary" variant="outlined" /> };
    return { label: "Passé", chip: <Chip label="Passé" size="small" color="default" variant="outlined" /> };
};

const usesUrssafStatus = (course: any, config: CourseTypeConfig): boolean =>
    config.showUrssafFilter || hasBlockingDeclarationStatus(course?.declarationStatus);

// Même valeur que dans ListCourse (`any`) : les helpers de statut attendent une chaîne mais acceptent null.
const errorsDescription = (course: any): any => course?.errorsStatusDescription?.[0]?.description ?? null;

/** Pastille de statut de la ligne, comme dans la liste actuelle. */
export const courseStatusNode = (course: any, config: CourseTypeConfig, classes: any): React.ReactNode => {
    if (usesUrssafStatus(course, config)) {
        return displayStatutCourse(course?.urssafServiceStatus, course?.statusDescription, errorsDescription(course), course?.isOver, false, classes, course?.declarationStatus, course?.declarationStatusReason);
    }
    return dateBasedStatus(course, config.isCoursePass).chip;
};

/** Libellé court du statut (« Validé », « En attente », « À venir ») pour les résumés de groupe. */
export const courseStatusLabel = (course: any, config: CourseTypeConfig): string => {
    if (usesUrssafStatus(course, config)) {
        return resolveStatutCourse(course?.urssafServiceStatus, course?.statusDescription, errorsDescription(course), course?.isOver, course?.declarationStatus, course?.declarationStatusReason).text;
    }
    return dateBasedStatus(course, config.isCoursePass).label;
};

/** « 1 en attente, 1 validé, 1 à venir » ; « 2 validés » */
export const statusSummary = (courses: any[], config: CourseTypeConfig): string => {
    const counts = new Map<string, number>();
    courses.forEach((c) => {
        const label = courseStatusLabel(c, config);
        counts.set(label, (counts.get(label) ?? 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([label, n]) => {
            const lower = label.charAt(0).toLowerCase() + label.slice(1);
            const pluralised = n > 1 && /ée?$/.test(lower) ? `${lower}s` : lower;
            return `${n} ${pluralised}`;
        })
        .join(", ");
};

/**
 * Données de la fiche du cours (`DetailCoursV2`), reprises de `ListCourse` :
 * mêmes montants, même statut détaillé, mêmes documents.
 */
export const courseDetailProps = (course: any, teachr: any, config: CourseTypeConfig, classes: any): CoursProps => {
    const c = course as any;
    const fullName = [c?.forChildFirstName, c?.forChildLastName].filter(Boolean).join(" ") || [c?.firstName, c?.lastName].filter(Boolean).join(" ");
    const startDate = moment(c?.startDatetime).format("DD MMM YYYY");
    const endDate = moment(c?.endDatetime).format("DD MMM YYYY");
    const prixTotal = vtCoursePrixTotal(c);
    const remuneration = isVerifiedTrainer() ? vtCourseRemuneration(c) : Number(c?.priceCf?.priceNoFees ?? c?.amount?.priceNoFees ?? 0);
    // relation Teach'r : pas de prix total ni de part client, ils comprennent la part de la plateforme
    const showTotal = isVerifiedTrainer() && !isRelationTeachrCourse(c);
    const depositStay = Math.max((c?.priceCf?.priceWithFees ?? 0) / 2 - (c?.deposit ?? 0), 0).toFixed(2);
    return {
        eleve: fullName,
        matiere: c?.subjectAsked,
        date: c?.isPunctualMonth ? `du ${startDate} au ${endDate}` : `pour le ${startDate}`,
        heure: moment(c?.startDatetime).format("HH:mm"),
        duree: Functions.renderDuration(c?.duration),
        remuneration: remuneration.toFixed(2),
        prixTotal: showTotal ? prixTotal.toFixed(2) : undefined,
        payeParClient: showTotal ? vtClientShare(prixTotal).toFixed(2) : undefined,
        tvaAmount: c?.teachrVat?.vatAmount,
        tvaRate: c?.teachrVat?.vatRate,
        acompte: c?.deposit,
        format: c?.isPunctualMonth
            ? `Plusieurs ${getActivePrestationBrandConfig("s")}`
            : capitalizeFirstLetter(getActivePrestationBrandConfig(" ponctuelle", "", "", " ponctuel")),
        infos: c?.globalExtraInformation,
        sessionDescription: c?.sessionDescriptionOverride ?? null,
        teachrId: teachr?.id ?? null,
        reste: depositStay,
        proofDeposit: c?.proofDeposit?.contentUrl ?? "",
        proofDeposits: c?.isPunctualMonth && Array.isArray(c?.proofDeposits)
            ? c.proofDeposits.map((p: any) => p?.contentUrl).filter(Boolean)
            : undefined,
        isCoursePass: config.isCoursePass,
        punctual: c,
        displayStatut: usesUrssafStatus(c, config)
            ? displayStatutCourseDetail(c?.urssafServiceStatus, c?.statusDescription, errorsDescription(c), c?.isOver, classes, c?.declarationStatus, c?.declarationStatusReason, "left")
            : dateBasedStatus(c, config.isCoursePass).chip,
        depositDate: c?.depositDate ? moment(c.depositDate).toDate() : undefined,
    };
};
